import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, allComics } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');

  const currentCategory = categories.find((cat) => cat.id === slug) || categories[0];

  const filteredComics = allComics.filter(
    (comic) => {
      if (slug === 'all') return true;
      return comic.category === currentCategory.name;
    }
  );

  const sortedComics = [...filteredComics].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'latest') return b.chapters - a.chapters;
    return b.rating - a.rating;
  });

  return (
    <main className="min-h-screen bg-background-50">
      {/* JSON-LD: WebPage + BreadcrumbList + ItemList */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": `${currentCategory.name} - 韩漫漫画`,
        "url": 'https://www.example.com/category/all',
        "description": `探索${currentCategory.name}类韩漫，共${currentCategory.count}部精彩作品`,
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "首页",
              "item": "https://www.example.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": currentCategory.name,
              "item": 'https://www.example.com/category/all'
            }
          ]
        }
      })}} />
      <div className="bg-foreground-900">
        <Navbar />
        <div className="pt-20 pb-16 px-4 md:px-6 text-center">
          <nav className="flex items-center justify-center gap-2 text-xs text-foreground-300 mb-6">
            <Link to="/" className="hover:text-white transition-colors whitespace-nowrap">首页</Link>
            <span>/</span>
            <span className="text-white whitespace-nowrap">{currentCategory.name}</span>
          </nav>
          <p className="text-foreground-400 text-xs uppercase tracking-widest mb-3">
            分类浏览
          </p>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4">
            {currentCategory.name}
          </h1>
          <p className="text-foreground-300 text-sm max-w-lg mx-auto mb-8">
            探索 {currentCategory.name} 类韩漫，共 {currentCategory.count} 部精彩作品等你来读
          </p>

          <div className="inline-flex items-center gap-2 bg-foreground-800 rounded-full p-1">
            {[
              { key: 'popular' as const, label: '最受欢迎' },
              { key: 'latest' as const, label: '最新更新' },
              { key: 'rating' as const, label: '评分最高' },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`whitespace-nowrap px-5 py-2 rounded-full text-xs font-medium transition-all cursor-pointer ${
                  sortBy === opt.key
                    ? 'bg-primary-500 text-white'
                    : 'text-foreground-300 hover:text-white'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-6 pb-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to="/category/all"
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  cat.id === slug
                    ? 'bg-primary-500/20 text-primary-300 border border-primary-500/30'
                    : 'text-foreground-300 hover:text-white hover:bg-foreground-800 border border-transparent'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <section className="py-12 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedComics.map((comic) => (
              <Link
                key={comic.id}
                to={`/article/${comic.id}`}
                className="group bg-background-50 rounded-xl overflow-hidden flex cursor-pointer hover:bg-background-100 transition-colors"
              >
                <div className="w-28 md:w-36 flex-shrink-0">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="flex-1 p-4 flex flex-col justify-between">
                  <div>
                    <span className="inline-block px-2 py-0.5 rounded-md bg-accent-100 text-accent-700 text-[10px] font-medium mb-2 whitespace-nowrap">
                      {comic.category}
                    </span>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-2">
                      {comic.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-foreground-400">
                    <span>{comic.author}</span>
                    <span>{comic.chapters}话</span>
                    <span className="flex items-center gap-1 text-accent-500">
                      <i className="ri-star-fill text-[10px]"></i>
                      {comic.rating}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {sortedComics.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
                <i className="ri-book-open-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">暂无该分类的漫画</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}